import React, { useRef, useEffect } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import { OrbitControls, Text, Html } from '@react-three/drei';
import * as THREE from 'three';
import { Hall1Machines, Hall3Machines, Hall4Machines } from './MachineModels';
import { DepositInterior } from './WarehouseInterior';
import { Hall1ProductionBobins, Hall3ProductionBobins, Hall4ProductionBobins } from './ProductionBobins';
import { Hall1LiveDrums, Hall3LiveDrums, Hall4LiveDrums } from './LiveMachineDrums';
import { YardVisualization } from './YardVisualization';

// ─── CONSTANTS ─────────────────────────────────────────────
const WT = 0.3;

const C = {
  wall: '#CBD0D5',
  wallInternal: '#B8BDC2',
  roof: '#4B6A7A',
  floor: '#A8A8A8',
  floorInterior: '#B5B5B5',
  foundation: '#7A7A7A',
  door: '#2A2A2A',
  road: '#484848',
  ground: '#5A8A48',
  gatePillar: '#686868',
  gateBeam: '#585858',
  accent1: '#5B9BD5',
  accent2: '#70AD47',
  accent3: '#ED7D31',
  accent4: '#FFC000',
  accentEntrance: '#9B59B6',
  portal: '#FFD700'
};

// ─── BUILDING DATA ─────────────────────────────────────────
// All buildings ~50% wider for spacious interiors
// Road: X=-4 to X=11 (15m)

export const HALL_1 = {
  id: 'hall-1',
  label: 'Hall 1',
  detail: '82.7m x 24m',
  xStart: -28, xEnd: -4,
  zStart: 0, zEnd: 82.7,
  height: 4.5,
  doors: [{ wall: 'right', pos: 46.5, w: 4, h: 3.5 }],
  accent: C.accent1
};

export const HALL_2 = {
  id: 'hall-2',
  label: 'Hall 2',
  detail: '38.5m x 11.5m',
  xStart: -28, xEnd: 10.5,
  zStart: 82.7, zEnd: 82.7 + 11.5,
  height: 4.5,
  doors: [{ wall: 'front', pos: -21 + 17.2, w: 4, h: 3.5 }],
  accent: C.accent2
};

export const RIGHT_BUILDING = {
  id: 'hall-right',
  xStart: 11, xEnd: 35,
  zStart: 0, zEnd: 82.3,
  height: 4.5,
  doors: [
    { wall: 'left', pos: 17, w: 4, h: 3.5 },
    { wall: 'left', pos: 44.15, w: 4, h: 3.5 }
  ],
  internalWalls: [
    { z: 41, doors: [] },
    { z: 47.3, doors: [{ pos: 23, w: 4, h: 3.5 }] }
  ],
  sections: [
    { id: 'hall-3', label: 'Hall 3', detail: '41m x 24m', zStart: 0, zEnd: 41, accent: C.accent3 },
    { id: 'entrance', label: 'Entrance', detail: '6.3m', zStart: 41, zEnd: 47.3, accent: C.accentEntrance },
    { id: 'hall-4', label: 'Hall 4', detail: '35m x 24m', zStart: 47.3, zEnd: 82.3, accent: C.accent4 }
  ]
};

// Deposit 1 — 33m x 37m, PARALLEL to Hall4+Entrance, road between
// Right building ends at X=28, ~8m road, then Deposit 1 at X=36
export const DEPOSIT_1 = {
  id: 'deposit-1',
  label: 'Deposit 1',
  detail: '33m x 37m',
  xStart: 43, xEnd: 76,
  zStart: 41, zEnd: 78,
  height: 7,
  doors: [{ wall: 'left', pos: 55, w: 4, h: 3.5 }],
  accent: '#E67E22'

};

// Deposit 2 — 32m x 13m, after Deposit 1 with 9m road
export const DEPOSIT_2 = {
  id: 'deposit-2',
  label: 'Deposit 2',
  detail: '32m x 13m',
  xStart: 43, xEnd: 75,
  zStart: 87, zEnd: 100,
  height: 7,
  doors: [{ wall: 'left', pos: 92, w: 3, h: 3.5 }],
  accent: '#8E44AD'
};

export const SIMPLE_BUILDINGS = [HALL_1, HALL_2, DEPOSIT_1, DEPOSIT_2];

// ─── CAMERA VIEWS ──────────────────────────────────────────

export const BUILDING_VIEWS = {
  'hall-1':      { focus: { pos: [14, 20, 41],   look: [-16, 0, 41] } },
  'hall-2':      { focus: { pos: [-8, 20, 75],   look: [-8, 0, 88.5] } },
  'hall-right':  { focus: { pos: [-8, 20, 41],   look: [23, 0, 41] } },
  'deposit-1':   { focus: { pos: [30, 30, 60],   look: [59, 0, 60] } },
  'deposit-2':   { focus: { pos: [30, 25, 93],   look: [59, 0, 93] } }
};

export const INTERIOR_VIEWS = {
  'hall-1':      { pos: [-7, 2.5, 48],    look: [-8, 2, 70] },
  'hall-2':      { pos: [-3, 2.5, 87],    look: [-5, 2, 90] },
  'entrance':    { pos: [20, 2.5, 44.15], look: [23, 2, 45.5] },
  'hall-3':      { pos: [20, 2.5, 17],    look: [23, 2, 35] },
  'hall-4':      { pos: [20, 2.5, 55],    look: [23, 2, 70] },
  'deposit-1':   { pos: [47, 3, 44],      look: [59, 2, 60] },
  'deposit-2':   { pos: [47, 3, 90],      look: [59, 2, 95] }
};

// ─── DOOR PORTALS ──────────────────────────────────────────

export const DOOR_PORTALS = {
  'hall-1': [
    { pos: [-4, 2.25, 46.5], rot: [0, Math.PI / 2, 0], label: 'Exit to Road', action: 'exit' }
  ],
  'hall-2': [
    { pos: [-3.8, 2.25, 82.7], rot: [0, 0, 0], label: 'Exit to Road', action: 'exit' }
  ],
  'entrance': [
    { pos: [11, 2.25, 44.15], rot: [0, Math.PI / 2, 0], label: 'Exit to Road', action: 'exit' },
    { pos: [23, 2.25, 47.3], rot: [0, 0, 0], label: 'Enter Hall 4 →', action: 'hall-4' }
  ],
  'hall-4': [
    { pos: [23, 2.25, 47.3], rot: [0, Math.PI, 0], label: '← Back to Entrance', action: 'entrance' },
    { pos: [11, 2.25, 65], rot: [0, Math.PI / 2, 0], label: 'Exit to Road', action: 'exit' }
  ],
  'hall-3': [
    { pos: [11, 2.25, 17], rot: [0, Math.PI / 2, 0], label: 'Exit to Road', action: 'exit' }
  ],
  'deposit-1': [
    { pos: [43, 2.25, 55], rot: [0, Math.PI / 2, 0], label: 'Exit Deposit 1', action: 'exit' }
  ],
  'deposit-2': [
    { pos: [43, 2.25, 92], rot: [0, Math.PI / 2, 0], label: 'Exit Deposit 2', action: 'exit' }
  ]
};

// ─── WALL HELPERS ──────────────────────────────────────────

function getWallSegments(start, end, height, doors) {
  if (!doors || !doors.length)
    return [{ from: start, to: end, y0: 0, y1: height }];
  const segs = [];
  const sorted = [...doors].sort((a, b) => a.pos - b.pos);
  let cursor = start;
  for (const d of sorted) {
    const ds = d.pos - d.w / 2;
    const de = d.pos + d.w / 2;
    if (ds > cursor + 0.01) segs.push({ from: cursor, to: ds, y0: 0, y1: height });
    if (d.h < height) segs.push({ from: ds, to: de, y0: d.h, y1: height });
    cursor = de;
  }
  if (cursor < end - 0.01) segs.push({ from: cursor, to: end, y0: 0, y1: height });
  return segs;
}

function WallMeshes({ start, end, fixedPos, fixedAxis, height, doors, color }) {
  const segs = getWallSegments(start, end, height, doors);
  return segs.map((s, i) => {
    const len = s.to - s.from;
    const h = s.y1 - s.y0;
    if (len < 0.01 || h < 0.01) return null;
    const mid = (s.from + s.to) / 2;
    const yMid = (s.y0 + s.y1) / 2;
    const pos = fixedAxis === 'x' ? [fixedPos, yMid, mid] : [mid, yMid, fixedPos];
    const args = fixedAxis === 'x' ? [WT, h, len] : [len, h, WT];
    return (
      <mesh key={i} position={pos} castShadow receiveShadow>
        <boxGeometry args={args} />
        <meshStandardMaterial color={color} roughness={0.85} metalness={0.05} />
      </mesh>
    );
  });
}

function DoorFrames({ doors, fixedPos, fixedAxis }) {
  const FT = 0.12;
  return doors.map((d, i) => {
    const hw = d.w / 2;
    if (fixedAxis === 'x') {
      return (
        <React.Fragment key={i}>
          <mesh position={[fixedPos, d.h / 2, d.pos - hw]}><boxGeometry args={[WT + 0.1, d.h, FT]} /><meshStandardMaterial color={C.door} /></mesh>
          <mesh position={[fixedPos, d.h / 2, d.pos + hw]}><boxGeometry args={[WT + 0.1, d.h, FT]} /><meshStandardMaterial color={C.door} /></mesh>
          <mesh position={[fixedPos, d.h, d.pos]}><boxGeometry args={[WT + 0.1, FT, d.w + FT * 2]} /><meshStandardMaterial color={C.door} /></mesh>
        </React.Fragment>
      );
    }
    return (
      <React.Fragment key={i}>
        <mesh position={[d.pos - hw, d.h / 2, fixedPos]}><boxGeometry args={[FT, d.h, WT + 0.1]} /><meshStandardMaterial color={C.door} /></mesh>
        <mesh position={[d.pos + hw, d.h / 2, fixedPos]}><boxGeometry args={[FT, d.h, WT + 0.1]} /><meshStandardMaterial color={C.door} /></mesh>
        <mesh position={[d.pos, d.h, fixedPos]}><boxGeometry args={[d.w + FT * 2, FT, WT + 0.1]} /><meshStandardMaterial color={C.door} /></mesh>
      </React.Fragment>
    );
  });
}

// ─── DOOR PORTAL (clickable door marker) ───────────────────

function DoorPortal({ pos, rot, label, onClick }) {
  return (
    <group position={pos} rotation={rot}>
      {/* Clickable door rectangle */}
      <mesh onClick={e => { e.stopPropagation(); onClick(); }}>
        <planeGeometry args={[3.6, 4.2]} />
        <meshBasicMaterial color={C.portal} transparent opacity={0.12} side={THREE.DoubleSide} />
      </mesh>
      <mesh position={[0, 0, -0.02]}>
        <planeGeometry args={[3.8, 4.4]} />
        <meshBasicMaterial color={C.portal} transparent opacity={0.06} side={THREE.DoubleSide} />
      </mesh>
      {/* Label — onClick on the DOM div directly */}
      <Html center position={[0, 0, 0.1]}>
        <div
          onClick={onClick}
          style={{
            cursor: 'pointer',
            background: 'rgba(0,0,0,0.8)',
            color: C.portal,
            padding: '8px 18px',
            borderRadius: 6,
            fontSize: 14,
            fontWeight: 700,
            border: `2px solid ${C.portal}`,
            userSelect: 'none',
            whiteSpace: 'nowrap',
            textShadow: '0 0 8px rgba(255,215,0,0.5)',
            boxShadow: '0 0 20px rgba(255,215,0,0.3)'
          }}
        >
          {label}
        </div>
      </Html>
    </group>
  );
}

// ─── FLOOR MARKINGS ────────────────────────────────────────

function FloorMarkings({ xStart, xEnd, zStart, zEnd }) {
  const cx = (xStart + xEnd) / 2;
  const w = xEnd - xStart;
  const markings = [];
  markings.push(
    <mesh key="cl" rotation={[-Math.PI / 2, 0, 0]} position={[cx, 0.23, (zStart + zEnd) / 2]}>
      <planeGeometry args={[0.08, zEnd - zStart - 2]} />
      <meshBasicMaterial color="#888" opacity={0.4} transparent />
    </mesh>
  );
  for (let z = zStart + 10; z < zEnd; z += 10) {
    markings.push(
      <mesh key={`cr-${z}`} rotation={[-Math.PI / 2, 0, 0]} position={[cx, 0.23, z]}>
        <planeGeometry args={[w - 2, 0.06]} />
        <meshBasicMaterial color="#888" opacity={0.3} transparent />
      </mesh>
    );
  }
  const bw = 0.5, bc = '#D4A017', bo = 0.25;
  markings.push(
    <mesh key="bl" rotation={[-Math.PI / 2, 0, 0]} position={[xStart + bw / 2 + 0.2, 0.23, (zStart + zEnd) / 2]}>
      <planeGeometry args={[bw, zEnd - zStart - 1]} /><meshBasicMaterial color={bc} opacity={bo} transparent />
    </mesh>
  );
  markings.push(
    <mesh key="br" rotation={[-Math.PI / 2, 0, 0]} position={[xEnd - bw / 2 - 0.2, 0.23, (zStart + zEnd) / 2]}>
      <planeGeometry args={[bw, zEnd - zStart - 1]} /><meshBasicMaterial color={bc} opacity={bo} transparent />
    </mesh>
  );
  return <>{markings}</>;
}

// ─── STANDARD BUILDING ─────────────────────────────────────

function Building({ config, selected, viewMode, onSelect, visible = true }) {
  const { id, label, detail, xStart, xEnd, zStart, zEnd, height, doors, accent } = config;
  if (!visible) return null;

  const cx = (xStart + xEnd) / 2;
  const cz = (zStart + zEnd) / 2;
  const w = xEnd - xStart;
  const l = zEnd - zStart;

  const leftDoors = doors.filter(d => d.wall === 'left');
  const rightDoors = doors.filter(d => d.wall === 'right');
  const frontDoors = doors.filter(d => d.wall === 'front');
  const backDoors = doors.filter(d => d.wall === 'back');

  const isSelected = selected === id;
  const isInside = isSelected && viewMode === 'interior';
  const wallColor = isSelected ? '#E8D0C0' : C.wall;

  let roofOpacity = 0.85;
  if (isInside) roofOpacity = 0.06;
  else if (isSelected) roofOpacity = 0.35;

  return (
    <group onClick={e => { e.stopPropagation(); onSelect(id, e.point); }}>
      <mesh position={[cx, 0.1, cz]} receiveShadow>
        <boxGeometry args={[w + 0.6, 0.2, l + 0.6]} />
        <meshStandardMaterial color={C.foundation} roughness={0.95} />
      </mesh>
      <mesh position={[cx, 0.21, cz]} receiveShadow>
        <boxGeometry args={[w - 0.1, 0.02, l - 0.1]} />
        <meshStandardMaterial color={isInside ? C.floorInterior : C.floor} roughness={0.9} />
      </mesh>
      {isInside && <FloorMarkings xStart={xStart} xEnd={xEnd} zStart={zStart} zEnd={zEnd} />}

      <WallMeshes start={zStart} end={zEnd} fixedPos={xStart} fixedAxis="x" height={height} doors={leftDoors} color={wallColor} />
      <WallMeshes start={zStart} end={zEnd} fixedPos={xEnd} fixedAxis="x" height={height} doors={rightDoors} color={wallColor} />
      <WallMeshes start={xStart} end={xEnd} fixedPos={zStart} fixedAxis="z" height={height} doors={frontDoors} color={wallColor} />
      <WallMeshes start={xStart} end={xEnd} fixedPos={zEnd} fixedAxis="z" height={height} doors={backDoors} color={wallColor} />

      <DoorFrames doors={leftDoors} fixedPos={xStart} fixedAxis="x" />
      <DoorFrames doors={rightDoors} fixedPos={xEnd} fixedAxis="x" />
      <DoorFrames doors={frontDoors} fixedPos={zStart} fixedAxis="z" />
      <DoorFrames doors={backDoors} fixedPos={zEnd} fixedAxis="z" />

      <mesh position={[cx, height + 0.1, cz]} castShadow receiveShadow>
        <boxGeometry args={[w + 0.8, 0.2, l + 0.8]} />
        <meshStandardMaterial color={C.roof} roughness={0.6} metalness={0.3} transparent opacity={roofOpacity} />
      </mesh>
      <mesh position={[cx, height + 0.22, cz]}>
        <boxGeometry args={[w + 0.9, 0.04, l + 0.9]} />
        <meshStandardMaterial color={accent} metalness={0.5} roughness={0.4} transparent opacity={isInside ? 0.08 : 1} />
      </mesh>

      {!isInside && (
        <Html position={[cx, height + 3, cz]} center>
          <div style={{
            background: isSelected ? accent : 'rgba(30,30,30,0.85)',
            color: 'white', padding: '4px 12px', borderRadius: 6,
            fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
            borderLeft: `3px solid ${accent}`,
            userSelect: 'none', pointerEvents: 'none'
          }}>
            {label}
            <span style={{ fontWeight: 400, fontSize: 11, marginLeft: 6, opacity: 0.8 }}>{detail}</span>
            {isSelected && viewMode === 'focus' && (
              <span style={{ fontSize: 10, marginLeft: 8, opacity: 0.6 }}>click to enter</span>
            )}
          </div>
        </Html>
      )}
    </group>
  );
}

// ─── RIGHT BUILDING (Hall 3 + Entrance + Hall 4) ───────────

function RightBuildingComp({ selected, viewMode, onSelect, visible = true }) {
  if (!visible) return null;
  const b = RIGHT_BUILDING;
  const { xStart, xEnd, zStart, zEnd, height, doors, internalWalls, sections } = b;
  const cx = (xStart + xEnd) / 2;
  const cz = (zStart + zEnd) / 2;
  const w = xEnd - xStart;
  const l = zEnd - zStart;

  const isSelected = selected === 'hall-right';
  const isInside = isSelected && viewMode === 'interior';
  const wallColor = isSelected ? '#E8D0C0' : C.wall;

  const leftDoors = doors.filter(d => d.wall === 'left');
  const rightDoors = doors.filter(d => d.wall === 'right');

  let roofOpacity = 0.85;
  if (isInside) roofOpacity = 0.06;
  else if (isSelected) roofOpacity = 0.35;

  return (
    <group onClick={e => { e.stopPropagation(); onSelect('hall-right', e.point); }}>
      <mesh position={[cx, 0.1, cz]} receiveShadow>
        <boxGeometry args={[w + 0.6, 0.2, l + 0.6]} />
        <meshStandardMaterial color={C.foundation} roughness={0.95} />
      </mesh>
      <mesh position={[cx, 0.21, cz]} receiveShadow>
        <boxGeometry args={[w - 0.1, 0.02, l - 0.1]} />
        <meshStandardMaterial color={isInside ? C.floorInterior : C.floor} roughness={0.9} />
      </mesh>
      {isInside && <FloorMarkings xStart={xStart} xEnd={xEnd} zStart={zStart} zEnd={zEnd} />}

      {/* Outer walls */}
      <WallMeshes start={zStart} end={zEnd} fixedPos={xStart} fixedAxis="x" height={height} doors={leftDoors} color={wallColor} />
      <WallMeshes start={zStart} end={zEnd} fixedPos={xEnd} fixedAxis="x" height={height} doors={rightDoors} color={wallColor} />
      <WallMeshes start={xStart} end={xEnd} fixedPos={zStart} fixedAxis="z" height={height} doors={[]} color={wallColor} />
      <WallMeshes start={xStart} end={xEnd} fixedPos={zEnd} fixedAxis="z" height={height} doors={[]} color={wallColor} />

      <DoorFrames doors={leftDoors} fixedPos={xStart} fixedAxis="x" />
      <DoorFrames doors={rightDoors} fixedPos={xEnd} fixedAxis="x" />

      {/* Internal walls */}
      {internalWalls.map((iw, idx) => (
        <React.Fragment key={`iw-${idx}`}>
          <WallMeshes start={xStart} end={xEnd} fixedPos={iw.z} fixedAxis="z" height={height} doors={iw.doors} color={isSelected ? '#DCC5B5' : C.wallInternal} />
          {iw.doors.length > 0 && <DoorFrames doors={iw.doors} fixedPos={iw.z} fixedAxis="z" />}
        </React.Fragment>
      ))}

      {/* Roof */}
      <mesh position={[cx, height + 0.1, cz]} castShadow receiveShadow>
        <boxGeometry args={[w + 0.8, 0.2, l + 0.8]} />
        <meshStandardMaterial color={C.roof} roughness={0.6} metalness={0.3} transparent opacity={roofOpacity} />
      </mesh>
      {sections.map(sec => (
        <mesh key={sec.id} position={[cx, height + 0.22, (sec.zStart + sec.zEnd) / 2]}>
          <boxGeometry args={[w + 0.9, 0.04, sec.zEnd - sec.zStart - 0.3]} />
          <meshStandardMaterial color={sec.accent} metalness={0.5} roughness={0.4} transparent opacity={isInside ? 0.08 : 1} />
        </mesh>
      ))}

      {/* Labels — hidden when inside */}
      {!isInside && sections.map(sec => (
        <Html key={sec.id} position={[cx, height + 3, (sec.zStart + sec.zEnd) / 2]} center>
          <div style={{
            background: isSelected ? sec.accent : 'rgba(30,30,30,0.85)',
            color: 'white', padding: '4px 12px', borderRadius: 6,
            fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
            borderLeft: `3px solid ${sec.accent}`,
            userSelect: 'none', pointerEvents: 'none'
          }}>
            {sec.label}
            <span style={{ fontWeight: 400, fontSize: 11, marginLeft: 6, opacity: 0.8 }}>{sec.detail}</span>
            {isSelected && viewMode === 'focus' && sec.id === 'entrance' && (
              <span style={{ fontSize: 10, marginLeft: 8, opacity: 0.6 }}>click to enter</span>
            )}
          </div>
        </Html>
      ))}
    </group>
  );
}

// ─── GATE ──────────────────────────────────────────────────

function Gate() {
  const pH = 5, pW = 0.6, gW = 8;
  return (
    <group position={[3.5, 0, -1]}>
      <mesh position={[-gW / 2, pH / 2, 0]} castShadow><boxGeometry args={[pW, pH, pW]} /><meshStandardMaterial color={C.gatePillar} roughness={0.7} /></mesh>
      <mesh position={[gW / 2, pH / 2, 0]} castShadow><boxGeometry args={[pW, pH, pW]} /><meshStandardMaterial color={C.gatePillar} roughness={0.7} /></mesh>
      <mesh position={[0, pH + 0.2, 0]} castShadow><boxGeometry args={[gW + pW, 0.4, pW]} /><meshStandardMaterial color={C.gateBeam} roughness={0.6} metalness={0.3} /></mesh>
      <Html position={[0, pH + 1.5, 0]} center>
        <div style={{ background: 'rgba(40,40,40,0.9)', color: '#FFD700', padding: '4px 16px', borderRadius: 4, fontSize: 14, fontWeight: 700, letterSpacing: 2, userSelect: 'none', pointerEvents: 'none' }}>
          ENTRANCE
        </div>
      </Html>
    </group>
  );
}

// ─── GROUND & ROAD ─────────────────────────────────────────

function GroundAndRoad() {
  const roadCx = (-4 + 11) / 2; // 3.5
  const roadW = 15;
  return (
    <>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[3.5, -0.01, 45]} receiveShadow>
        <planeGeometry args={[300, 300]} /><meshStandardMaterial color={C.ground} roughness={1} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[roadCx, 0.02, 40]} receiveShadow>
        <planeGeometry args={[roadW, 100]} /><meshStandardMaterial color={C.road} roughness={0.95} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[roadCx, 0.03, 40]}>
        <planeGeometry args={[0.15, 100]} /><meshStandardMaterial color="#FFD700" />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[roadCx - roadW / 2 + 0.1, 0.03, 40]}>
        <planeGeometry args={[0.1, 100]} /><meshStandardMaterial color="#FFFFFF" opacity={0.5} transparent />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[roadCx + roadW / 2 - 0.1, 0.03, 40]}>
        <planeGeometry args={[0.1, 100]} /><meshStandardMaterial color="#FFFFFF" opacity={0.5} transparent />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[roadCx, 0.02, -15]} receiveShadow>
        <planeGeometry args={[roadW, 30]} /><meshStandardMaterial color={C.road} roughness={0.95} />
      </mesh>
    </>
  );
}

// ─── DIMENSION LABELS ──────────────────────────────────────

function DimensionLabels({ viewMode }) {
  if (viewMode === 'interior') return null;
  const rb = RIGHT_BUILDING;
  return (
    <>
      <Text position={[HALL_1.xStart - 2.5, 0.1, (HALL_1.zStart + HALL_1.zEnd) / 2]} rotation={[-Math.PI / 2, 0, Math.PI / 2]} fontSize={1.3} color="#2A2A2A" anchorX="center" anchorY="middle" font={undefined}>82.7m</Text>
      <Text position={[(HALL_1.xStart + HALL_1.xEnd) / 2, 0.1, HALL_1.zStart - 1.8]} rotation={[-Math.PI / 2, 0, 0]} fontSize={1} color="#2A2A2A" anchorX="center" anchorY="middle" font={undefined}>11.5m</Text>
      <Text position={[rb.xEnd + 2.5, 0.1, (rb.zStart + rb.zEnd) / 2]} rotation={[-Math.PI / 2, 0, -Math.PI / 2]} fontSize={1.3} color="#2A2A2A" anchorX="center" anchorY="middle" font={undefined}>82.3m</Text>
      <Text position={[(rb.xStart + rb.xEnd) / 2, 0.1, rb.zStart - 1.8]} rotation={[-Math.PI / 2, 0, 0]} fontSize={1} color="#2A2A2A" anchorX="center" anchorY="middle" font={undefined}>11.6m</Text>
      {rb.sections.map(sec => (
        <Text key={sec.id} position={[rb.xEnd + 1.5, 0.1, (sec.zStart + sec.zEnd) / 2]} rotation={[-Math.PI / 2, 0, -Math.PI / 2]} fontSize={0.8} color="#555" anchorX="center" anchorY="middle" font={undefined}>{(sec.zEnd - sec.zStart).toFixed(1)}m</Text>
      ))}
      <Text position={[(HALL_2.xStart + HALL_2.xEnd) / 2, 0.1, HALL_2.zEnd + 2]} rotation={[-Math.PI / 2, 0, 0]} fontSize={1.1} color="#2A2A2A" anchorX="center" anchorY="middle" font={undefined}>26m</Text>
      <Text position={[HALL_2.xEnd + 2, 0.1, (HALL_2.zStart + HALL_2.zEnd) / 2]} rotation={[-Math.PI / 2, 0, -Math.PI / 2]} fontSize={1} color="#2A2A2A" anchorX="center" anchorY="middle" font={undefined}>11.5m</Text>
    </>
  );
}

function ScaleBar() {
  return (
    <group position={[30, 0.05, -8]}>
      <mesh rotation={[-Math.PI / 2, 0, 0]}><planeGeometry args={[10, 0.2]} /><meshBasicMaterial color="#333" /></mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[-5, 0, 0]}><planeGeometry args={[0.15, 0.8]} /><meshBasicMaterial color="#333" /></mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[5, 0, 0]}><planeGeometry args={[0.15, 0.8]} /><meshBasicMaterial color="#333" /></mesh>
      <Text position={[0, 0.06, -1]} rotation={[-Math.PI / 2, 0, 0]} fontSize={0.9} color="#333" anchorX="center" font={undefined}>10m</Text>
    </group>
  );
}

function Compass() {
  return (
    <group position={[32, 0.05, 0]}>
      <Text position={[0, 0.06, -2.5]} rotation={[-Math.PI / 2, 0, 0]} fontSize={1.8} color="#C44" anchorX="center" font={undefined} fontWeight="bold">N</Text>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, -0.8]}><planeGeometry args={[0.15, 2.5]} /><meshBasicMaterial color="#C44" side={THREE.DoubleSide} /></mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, -2.2]}><circleGeometry args={[0.5, 3]} /><meshBasicMaterial color="#C44" side={THREE.DoubleSide} /></mesh>
    </group>
  );
}

// ─── WASD WALK CONTROLS ────────────────────────────────────

function WalkControls({ controlsRef, enabled, speed = 0.25 }) {
  const { camera } = useThree();
  const keys = useRef({});

  useEffect(() => {
    const onDown = e => { keys.current[e.key.toLowerCase()] = true; };
    const onUp = e => { keys.current[e.key.toLowerCase()] = false; };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
    };
  }, []);

  useFrame(() => {
    if (!enabled || !controlsRef.current) return;
    const k = keys.current;

    const forward = new THREE.Vector3();
    camera.getWorldDirection(forward);
    forward.y = 0;
    forward.normalize();

    const right = new THREE.Vector3();
    right.crossVectors(forward, new THREE.Vector3(0, 1, 0)).normalize();

    const move = new THREE.Vector3();
    if (k['w'] || k['arrowup']) move.add(forward);
    if (k['s'] || k['arrowdown']) move.sub(forward);
    if (k['d'] || k['arrowright']) move.add(right);
    if (k['a'] || k['arrowleft']) move.sub(right);

    if (move.length() > 0) {
      move.normalize().multiplyScalar(speed);
      camera.position.add(move);
      controlsRef.current.target.add(move);
      controlsRef.current.update();
    }
  });

  return null;
}

// ─── CAMERA CONTROLLER ─────────────────────────────────────

function CameraController({ target, triggerKey, controlsRef }) {
  const { camera } = useThree();
  const targetPos = useRef(null);
  const targetLook = useRef(null);
  const animating = useRef(false);

  useEffect(() => {
    if (!target) return;
    targetPos.current = new THREE.Vector3(...target.pos);
    targetLook.current = new THREE.Vector3(...target.look);
    animating.current = true;
  }, [target, triggerKey]);

  // Cancel the fly-in the moment the user takes control (WASD or orbit/drag),
  // so manual movement doesn't fight the animation and get yanked back to the
  // entry ("first") view. The initiating click fires 'start' on pointer-DOWN,
  // before flyTo re-arms the animation on pointer-UP, so a fresh fly is safe.
  useEffect(() => {
    const MOVE_KEYS = ['w', 'a', 's', 'd', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright'];
    const cancel = () => { animating.current = false; };
    const onKey = e => { if (MOVE_KEYS.includes(e.key.toLowerCase())) cancel(); };
    window.addEventListener('keydown', onKey);
    const controls = controlsRef.current;
    if (controls) controls.addEventListener('start', cancel);
    return () => {
      window.removeEventListener('keydown', onKey);
      if (controls) controls.removeEventListener('start', cancel);
    };
  }, [controlsRef]);

  useFrame(() => {
    if (!animating.current || !controlsRef.current) return;
    camera.position.lerp(targetPos.current, 0.07);
    controlsRef.current.target.lerp(targetLook.current, 0.07);
    controlsRef.current.update();
    if (camera.position.distanceTo(targetPos.current) < 0.3) {
      camera.position.copy(targetPos.current);
      controlsRef.current.target.copy(targetLook.current);
      controlsRef.current.update();
      animating.current = false;
    }
  });
  return null;
}

// ─── MAIN SCENE ────────────────────────────────────────────

export default function FactoryScene({
  selectedBuilding,
  onSelectBuilding,
  viewMode,
  interiorSection,
  onDoorClick,
  cameraTarget,
  cameraTrigger,
  machineData,
  onOpenPanel,
  panelOpen,
  onSelectBobinGroup,
  onSelectYardItem,
  focusToken,
  onFocusLocated
}) {
  const controlsRef = useRef();
  const isInterior = viewMode === 'interior';

  // When inside a building, hide the others
  const showHall1 = !isInterior || selectedBuilding === 'hall-1';
  const showHall2 = !isInterior || selectedBuilding === 'hall-2';
  const showRight = !isInterior || selectedBuilding === 'hall-right';
  const showDep1 = !isInterior || selectedBuilding === 'deposit-1';
  const showDep2 = !isInterior || selectedBuilding === 'deposit-2';

  // Get portals for current interior section
  const portals = isInterior && interiorSection ? (DOOR_PORTALS[interiorSection] || []) : [];

  return (
    <>
      <ambientLight intensity={isInterior ? 0.6 : 0.4} />
      <hemisphereLight args={['#87CEEB', '#5A8A48', 0.3]} />
      <directionalLight position={[30, 50, -20]} intensity={1.2} castShadow
        shadow-mapSize={[2048, 2048]}
        shadow-camera-left={-100} shadow-camera-right={100}
        shadow-camera-top={100} shadow-camera-bottom={-100}
        shadow-camera-near={0.5} shadow-camera-far={300}
      />
      <directionalLight position={[-20, 30, 60]} intensity={0.3} />
      {isInterior && <pointLight position={[3.5, 6, 45]} intensity={0.5} distance={60} />}

      <color attach="background" args={['#B8D4E8']} />
      <fog attach="fog" args={['#B8D4E8', isInterior ? 40 : 180, isInterior ? 80 : 350]} />

      <OrbitControls
        ref={controlsRef}
        makeDefault
        target={[3.5, 0, 45]}
        maxPolarAngle={isInterior ? Math.PI / 2 + 0.3 : Math.PI / 2 - 0.05}
        minDistance={isInterior ? 1 : 5}
        maxDistance={isInterior ? 50 : 250}
        enableDamping dampingFactor={0.08}
      />

      <CameraController target={cameraTarget} triggerKey={cameraTrigger} controlsRef={controlsRef} />
      <WalkControls controlsRef={controlsRef} enabled={true} speed={isInterior ? 0.25 : 0.5} />

      <GroundAndRoad />
      {!isInterior && <Gate />}

      {/* Production halls */}
      <Building config={HALL_1} selected={selectedBuilding} viewMode={viewMode} onSelect={onSelectBuilding} visible={showHall1} />
      <Building config={HALL_2} selected={selectedBuilding} viewMode={viewMode} onSelect={onSelectBuilding} visible={showHall2} />
      <RightBuildingComp selected={selectedBuilding} viewMode={viewMode} onSelect={onSelectBuilding} visible={showRight} />

      {/* Warehouse deposits */}
      <Building config={DEPOSIT_1} selected={selectedBuilding} viewMode={viewMode} onSelect={onSelectBuilding} visible={showDep1} />
      <Building config={DEPOSIT_2} selected={selectedBuilding} viewMode={viewMode} onSelect={onSelectBuilding} visible={showDep2} />

      {/* Exterior road between right building (X=28) and deposits (X=36) */}
      {!isInterior && (
        <>
          {/* Road running along Z between right building and deposits */}
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[39, 0.02, 50]} receiveShadow>
            <planeGeometry args={[8, 100]} />
            <meshStandardMaterial color={C.road} roughness={0.95} />
          </mesh>
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[39, 0.03, 50]}>
            <planeGeometry args={[0.12, 100]} />
            <meshStandardMaterial color="#FFD700" />
          </mesh>
          {/* Road between Deposit 1 and Deposit 2 (Z=78 to 87) */}
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[59, 0.02, 82.5]} receiveShadow>
            <planeGeometry args={[34, 9]} />
            <meshStandardMaterial color={C.road} roughness={0.95} />
          </mesh>
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[59, 0.03, 82.5]}>
            <planeGeometry args={[0.12, 9]} />
            <meshStandardMaterial color="#FFD700" />
          </mesh>
        </>
      )}

      {/* Door portals when inside */}
      {portals.map((p, i) => (
        <DoorPortal key={i} pos={p.pos} rot={p.rot} label={p.label} onClick={() => onDoorClick(p.action)} />
      ))}

      {/* Hall 1 machines */}
      {isInterior && selectedBuilding === 'hall-1' && (
        <>
          <Hall1Machines machineData={machineData} onOpenPanel={onOpenPanel} hideLabels={panelOpen} />
          {!panelOpen && <Hall1ProductionBobins onSelectGroup={onSelectBobinGroup} focusToken={focusToken} onFocusLocated={onFocusLocated} />}
          {!panelOpen && <Hall1LiveDrums machineData={machineData} />}
        </>
      )}

      {/* Hall 3 machines + coiling bobins */}
      {isInterior && interiorSection === 'hall-3' && (
        <>
          <Hall3Machines machineData={machineData} onOpenPanel={onOpenPanel} hideLabels={panelOpen} />
          {!panelOpen && <Hall3ProductionBobins onSelectGroup={onSelectBobinGroup} focusToken={focusToken} onFocusLocated={onFocusLocated} />}
          {!panelOpen && <Hall3LiveDrums machineData={machineData} />}
        </>
      )}

      {/* Hall 4 machines + armouring bobins */}
      {isInterior && interiorSection === 'hall-4' && (
        <>
          <Hall4Machines machineData={machineData} onOpenPanel={onOpenPanel} hideLabels={panelOpen} />
          {!panelOpen && <Hall4ProductionBobins onSelectGroup={onSelectBobinGroup} focusToken={focusToken} onFocusLocated={onFocusLocated} />}
          {!panelOpen && <Hall4LiveDrums machineData={machineData} />}
        </>
      )}

      {/* Warehouse deposit interiors */}
      {isInterior && selectedBuilding === 'deposit-1' && <DepositInterior buildingConfig={DEPOSIT_1} />}
      {isInterior && selectedBuilding === 'deposit-2' && <DepositInterior buildingConfig={DEPOSIT_2} />}

      {/* Yard: drums/pallets waiting outside. Not clickable while a panel is
          open, for the same reason the hall bobins are not: the click would
          land behind the overlay. */}
      {!isInterior && (
        <YardVisualization onSelectItem={panelOpen ? undefined : onSelectYardItem} />
      )}

      <DimensionLabels viewMode={viewMode} />
      {!isInterior && <ScaleBar />}
      {!isInterior && <Compass />}
    </>
  );
}
